/**
 * MSML HEALTH Design Tokens
 *
 * Single source of truth for all visual styling values.
 * Change these to redesign the UI without touching components.
 */

// ── Colors ───────────────────────────────────────────────
export const colors = {
  // Surfaces
  bg:           '#060608',
  bgRaised:     '#0A0B0D',
  bgCard:       '#0F1014',
  bgInput:      '#0F1014',
  bgHover:      'rgba(255,255,255,0.04)',

  // Borders
  border:       'rgba(255,255,255,0.06)',
  borderHover:  'rgba(255,255,255,0.12)',
  borderActive: 'rgba(46,121,255,0.4)',

  // Text
  textPrimary:   '#FFFFFF',
  textSecondary: '#A1A1AA',
  textMuted:     '#71717A',
  textDim:       '#52525B',

  // Brand
  accent:       '#2E79FF',
  accentHover:  '#5A9AFF',
  accentMuted:  'rgba(46,121,255,0.10)',

  // Semantic
  success:      '#10B981',
  successMuted: 'rgba(16,185,129,0.10)',
  warning:      '#F5A623',
  warningMuted: 'rgba(245,166,35,0.10)',
  error:        '#EF4444',
  errorMuted:   'rgba(239,68,68,0.10)',
  info:         '#3B82F6',
  infoMuted:    'rgba(59,130,246,0.10)',

  // Dimension palette
  dimensions: {
    agency:               '#2E79FF',
    clarity:              '#10B981',
    emotional_regulation: '#EC4899',
    cognitive_load:       '#F5A623',
    safety:               '#8B5CF6',
    dependency:           '#EF4444',
  },

  // Score bands
  scoreBands: {
    excellent: '#10B981',
    good:      '#4C8DFF',
    acceptable:'#F5A623',
    poor:      '#EF4444',
  },

  // Verdict
  verdicts: {
    approved:     '#10B981',
    needs_review: '#F5A623',
    rejected:     '#EF4444',
  },
};

// ── Typography ───────────────────────────────────────────
export const typography = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontMono:   "'JetBrains Mono', 'Fira Code', monospace",

  sizes: {
    xs:   '0.625rem',   // 10px
    sm:   '0.75rem',    // 12px
    base: '0.875rem',   // 14px
    md:   '1rem',       // 16px
    lg:   '1.125rem',   // 18px
    xl:   '1.25rem',    // 20px
    '2xl':'1.5rem',     // 24px
    '3xl':'1.875rem',   // 30px
    '4xl':'2.25rem',    // 36px
  },

  weights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  leading: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// ── Spacing ──────────────────────────────────────────────
export const spacing = {
  px:  '1px',
  0:   '0',
  0.5: '0.125rem',
  1:   '0.25rem',
  1.5: '0.375rem',
  2:   '0.5rem',
  3:   '0.75rem',
  4:   '1rem',
  5:   '1.25rem',
  6:   '1.5rem',
  8:   '2rem',
  10:  '2.5rem',
  12:  '3rem',
  16:  '4rem',
};

// ── Radii ────────────────────────────────────────────────
export const radii = {
  sm:   '0.375rem',
  md:   '0.5rem',
  lg:   '0.75rem',
  xl:   '1rem',
  full: '9999px',
};

// ── Shadows ──────────────────────────────────────────────
export const shadows = {
  sm:   '0 1px 2px rgba(0,0,0,0.3)',
  md:   '0 4px 12px rgba(0,0,0,0.4)',
  lg:   '0 8px 24px rgba(0,0,0,0.5)',
  glow: (color) => `0 0 20px ${color}22, 0 0 40px ${color}11`,
};

// ── Transitions ──────────────────────────────────────────
export const transitions = {
  fast:    '150ms ease',
  normal:  '200ms ease',
  slow:    '300ms ease',
  spring:  '400ms cubic-bezier(0.34,1.56,0.64,1)',
};

// ── Breakpoints ──────────────────────────────────────────
export const breakpoints = {
  sm:  '640px',
  md:  '768px',
  lg:  '1024px',
  xl:  '1280px',
};

// ── Score helpers (derived from tokens) ──────────────────
export function getScoreBandColor(score) {
  if (score >= 85) return colors.scoreBands.excellent;
  if (score >= 70) return colors.scoreBands.good;
  if (score >= 50) return colors.scoreBands.acceptable;
  return colors.scoreBands.poor;
}

export function getScoreBandLabel(score) {
  if (score >= 85) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Acceptable';
  return 'Needs Improvement';
}

export function getScoreBandClass(score) {
  if (score >= 85) return 'msml-score-excellent';
  if (score >= 70) return 'msml-score-good';
  if (score >= 50) return 'msml-score-acceptable';
  return 'msml-score-poor';
}

export function getDimensionColor(key) {
  return colors.dimensions[key] || colors.accent;
}

export function getVerdictColor(verdict) {
  const v = (verdict || '').toUpperCase();
  if (v === 'APPROVED' || v === 'PASS') return colors.verdicts.approved;
  if (v === 'REJECTED' || v === 'FAIL') return colors.verdicts.rejected;
  return colors.verdicts.needs_review;
}

export function getVerdictBg(verdict) {
  const c = getVerdictColor(verdict);
  return c + '18'; // ~10% opacity hex
}
